import { AcceptanceCheckbox } from "../domain/AcceptanceCheckbox";
import {
  DocumentationAreaRepository,
  ExchangeSlugByDocumentationAreaIdAndActorIdResponse,
} from "../domain/DocumentationAreaRepository";
import { DocumentationAreaId } from "../domain/DocumentationAreaId";
import { DocumentationArea } from "../domain/DocumentationArea";
import { StorageFileWithContent } from "src/contexts/_shared/domain/StorageFileWithContent";
import { ActorBasicData } from "src/contexts/_shared/domain/ActorBasicData";
import { Customization } from "../domain/Customization";
import { LinkTypes } from "src/contexts/_shared/domain/LinkType";
import { ActorId } from "../domain/ActorId";
import { IdentityVerificationDocumentTypes } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentTypes";
import { AnalyticsProperties } from "src/apps/hooks/analytics/useSendAnalyticEvent";
import { OrderId } from "../domain/OrderId";
import { UserLinkId } from "src/contexts/documentationArea/domain/UserLinkId";
import { DocumentationAreaActorBasicData } from "../domain/DocumentationAreaActorBasicData";

type DocumentationAreaControllerOptions = {
  documentationAreaRepository: DocumentationAreaRepository;
};

export class DocumentationAreaController {
  private documentationAreaRepository: DocumentationAreaRepository;

  constructor({ documentationAreaRepository }: DocumentationAreaControllerOptions) {
    this.documentationAreaRepository = documentationAreaRepository;
  }

  public async get(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    locale: string | null,
  ): Promise<DocumentationArea> {
    return await this.documentationAreaRepository.getDocumentationArea(
      accessToken,
      documentationAreaId,
      actorId,
      locale,
    );
  }

  public async getCustomization(slug: string, linkType: LinkTypes): Promise<Customization> {
    return await this.documentationAreaRepository.getCustomization(slug, linkType);
  }

  public async getCustomizationByDocumentationAreaId(
    documentationAreaId: DocumentationAreaId,
    token: string,
  ): Promise<Customization> {
    return await this.documentationAreaRepository.getCustomizationByDocumentationAreaId(
      documentationAreaId,
      token,
    );
  }

  public async deliverNotification(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    notificationId: string,
  ): Promise<void> {
    await this.documentationAreaRepository.deliverNotification(
      accessToken,
      documentationAreaId,
      actorId,
      notificationId,
    );
  }

  public async getConsents(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
  ): Promise<AcceptanceCheckbox[]> {
    return await this.documentationAreaRepository.getConsents(accessToken, documentationAreaId);
  }

  public async getDocumentationAreaIdAndActorIdByMagicLinkSlug(
    accessToken: string,
    slug: string,
  ): Promise<ExchangeSlugByDocumentationAreaIdAndActorIdResponse> {
    return await this.documentationAreaRepository.getDocumentationAreaIdAndActorIdByMagicLinkSlug(
      accessToken,
      slug,
    );
  }

  public async getDocumentationAreaIdAndActorIdByUserLinkSlug(
    accessToken: string,
    slug: string,
  ): Promise<ExchangeSlugByDocumentationAreaIdAndActorIdResponse> {
    return await this.documentationAreaRepository.getDocumentationAreaIdAndActorIdByUserLinkSlug(
      accessToken,
      slug,
    );
  }

  public async getHasSignedAcceptance(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
  ): Promise<boolean> {
    return await this.documentationAreaRepository.getHasSignedAcceptance(
      accessToken,
      documentationAreaId,
      actorId,
    );
  }

  public async patchLocale(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    locale: string,
  ) {
    return await this.documentationAreaRepository.patchLocale(
      accessToken,
      documentationAreaId,
      actorId,
      locale,
    );
  }

  public async patchActorOrder(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    orderId: OrderId,
    files: StorageFileWithContent[],
  ) {
    return await this.documentationAreaRepository.patchActorOrder(
      accessToken,
      documentationAreaId,
      actorId,
      orderId,
      files,
    );
  }

  public async patchActorIDVOrder({
    accessToken,
    documentationAreaId,
    actorId,
    orderId,
    documentType,
    issuingCountry,
    frontFile,
    backFile,
  }: {
    accessToken: string;
    documentationAreaId: DocumentationAreaId;
    actorId: ActorId;
    orderId: OrderId;
    documentType?: IdentityVerificationDocumentTypes;
    issuingCountry?: string;
    frontFile?: StorageFileWithContent;
    backFile?: StorageFileWithContent;
  }) {
    return await this.documentationAreaRepository.patchActorIDVOrder(
      accessToken,
      documentationAreaId,
      actorId,
      orderId,
      documentType,
      issuingCountry,
      frontFile,
      backFile,
    );
  }

  public async createSignedAcceptances(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    signedAcceptanceCheckboxes: AcceptanceCheckbox[],
  ) {
    return await this.documentationAreaRepository.createSignedAcceptances(
      accessToken,
      documentationAreaId,
      actorId,
      signedAcceptanceCheckboxes,
    );
  }

  public async updateBasicData(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    basicData: ActorBasicData,
  ) {
    return await this.documentationAreaRepository.updateBasicData(
      accessToken,
      documentationAreaId,
      actorId,
      basicData,
    );
  }

  public async getActorBasicData(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
  ): Promise<DocumentationAreaActorBasicData> {
    return await this.documentationAreaRepository.getActorBasicData(
      accessToken,
      documentationAreaId,
      actorId,
    );
  }

  public async updateActorFromActorTemplateId(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    templateId: string,
  ): Promise<void> {
    return await this.documentationAreaRepository.updateActorFromActorTemplateId(
      accessToken,
      documentationAreaId,
      actorId,
      templateId,
    );
  }

  public async createActorInvitation(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    input: {
      email: string;
      actorId: ActorId;
    },
  ): Promise<void> {
    return await this.documentationAreaRepository.createActorInvitation(
      accessToken,
      documentationAreaId,
      input,
    );
  }

  public async sendActorUserLink(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    email: string,
  ): Promise<void> {
    return await this.documentationAreaRepository.sendActorUserLink(
      accessToken,
      documentationAreaId,
      actorId,
      email,
    );
  }

  public async deleteActorUser(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    userLinkId: string,
  ): Promise<void> {
    return await this.documentationAreaRepository.deleteActorUser(
      accessToken,
      documentationAreaId,
      actorId,
      userLinkId,
    );
  }

  public async getUserLink(slug: string) {
    return await this.documentationAreaRepository.getUserLink(slug);
  }

  public async createOpenAdminSession(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    locale: string,
  ) {
    return await this.documentationAreaRepository.createOpenAdminSession(
      accessToken,
      documentationAreaId,
      actorId,
      locale,
    );
  }

  public async createOpenAdminIDVSession(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    locale: string,
  ) {
    return await this.documentationAreaRepository.createOpenAdminIDVSession(
      accessToken,
      documentationAreaId,
      actorId,
      locale,
    );
  }

  public async discardIdentityVerificationStagedFile(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    identityVerificationId: string,
    fileId: string,
  ) {
    return await this.documentationAreaRepository.postDiscardIdentityVerificationStagedFile(
      accessToken,
      documentationAreaId,
      actorId,
      identityVerificationId,
      fileId,
    );
  }

  public async track(
    accessToken: string,
    documentationAreaId: DocumentationAreaId,
    actorId: ActorId,
    type: string,
    properties?: AnalyticsProperties,
    userProps?: AnalyticsProperties,
  ) {
    return await this.documentationAreaRepository.track(
      accessToken,
      documentationAreaId,
      actorId,
      type,
      properties,
      userProps,
    );
  }

  public async trackUserLink(
    userLinkId: UserLinkId,
    type: string,
    properties?: AnalyticsProperties,
    userProps?: AnalyticsProperties,
  ) {
    return await this.documentationAreaRepository.trackUserLink(
      userLinkId,
      type,
      properties,
      userProps,
    );
  }
}
